import { DesiredPlaceResultComponent, ParsedPlaceResult, ReducePlaceResultComponent } from './types'

export const desiredComponentTypes = [
  'administrative_area_level_1',
  'country',
  'locality',
  'postal_code',
  'postal_code_suffix',
  'postal_town',
  'route',
  'street_number',
  'sublocality_level_1',
] as const

export type DesiredComponentType = (typeof desiredComponentTypes)[number]

export const isDesiredComponentType = (compType: string): compType is DesiredComponentType =>
  (desiredComponentTypes as readonly string[]).includes(compType)

export function componentRestrictionsForCountry(
  countryCode: string,
): google.maps.places.ComponentRestrictions {
  if (countryCode === 'US') {
    return {
      // Google allows up to 5 country codes, US + 4 of the 5 territories (omitted Guam)
      country: ['us', 'as', 'mp', 'pr', 'vi'],
    }
  }
  return {
    country: [countryCode.toLowerCase()],
  }
}

export const reducePlaceResultComponentCommon: ReducePlaceResultComponent = (acc, component) => {
  switch (component.type) {
    case 'street_number':
      return {
        ...acc,
        address1: acc.address1 ? `${component.long_name} ${acc.address1}` : component.long_name,
      }
    case 'route':
      return {
        ...acc,
        address1: acc.address1 ? `${acc.address1} ${component.long_name}` : component.long_name,
      }
    case 'postal_code':
      return {
        ...acc,
        postalCode: acc.postalCode
          ? `${component.long_name}-${acc.postalCode}`
          : component.long_name,
      }
    case 'postal_code_suffix':
      return {
        ...acc,
        postalCode: acc.postalCode
          ? `${acc.postalCode}-${component.long_name}`
          : component.long_name,
      }
    case 'administrative_area_level_1':
      return {
        ...acc,
        region: component.long_name,
      }
    case 'country':
      return {
        ...acc,
        country: component.short_name,
      }
    default:
      return acc
  }
}

export const reducePlaceResultComponentPostalTown: ReducePlaceResultComponent = (
  acc,
  component,
) => {
  switch (component.type) {
    case 'postal_town':
      return {
        ...acc,
        city: component.long_name,
      }
    default:
      return acc
  }
}

export const reducePlaceResultComponentLocality: ReducePlaceResultComponent = (acc, component) => {
  switch (component.type) {
    case 'locality':
      return {
        ...acc,
        city: component.long_name,
      }
    default:
      return acc
  }
}

export const reducePlaceResultComponentSubLocalityLevel1: ReducePlaceResultComponent = (
  acc,
  component,
) => {
  switch (component.type) {
    case 'sublocality_level_1':
      return {
        ...acc,
        city: component.long_name,
      }
    default:
      return acc
  }
}

/**
 * Pipe places result component through each reducer
 */
export const reducePlaceResultComponentReducer =
  (component: DesiredPlaceResultComponent) =>
  (acc: ParsedPlaceResult, reducer: ReducePlaceResultComponent): ParsedPlaceResult =>
    reducer(acc, component)

/**
 * Identify appropriate reducers for populating address fields from places result
 */
export const reducersForPlaceResult = (
  components: google.maps.GeocoderAddressComponent[],
): ReducePlaceResultComponent[] => {
  const countryComponent = components.find((component) => component.types[0] === 'country')
  if (!countryComponent) {
    console.warn(`[parsePlacesResult] country not found in address components`)
  }
  const countryCode = countryComponent?.short_name?.toUpperCase()
  if (countryCode && ['GB', 'SE'].includes(countryCode)) {
    return [reducePlaceResultComponentCommon, reducePlaceResultComponentPostalTown]
  }
  const hasSublocalityLevel1 = components.find(
    (component) => component.types[0] === 'sublocality_level_1',
  )
  const hasLocality = components.find((component) => component.types[0] === 'locality')

  // Brooklyn and other parts of NYC use sublocality_level_1 instead of locality
  if (hasSublocalityLevel1 && !hasLocality) {
    return [reducePlaceResultComponentCommon, reducePlaceResultComponentSubLocalityLevel1]
  }

  return [reducePlaceResultComponentCommon, reducePlaceResultComponentLocality]
}

export const reducePlaceResultComponent =
  (reducers: ReducePlaceResultComponent[]) =>
  (acc: ParsedPlaceResult, component: google.maps.GeocoderAddressComponent): ParsedPlaceResult => {
    const { types, ...otherProps } = component
    // ignore components not applicable to address form
    const componentType = component.types.find(isDesiredComponentType)
    if (!componentType) {
      return acc
    }
    const desiredComponent: DesiredPlaceResultComponent = {
      ...otherProps,
      type: componentType,
    }
    return reducers.reduce(reducePlaceResultComponentReducer(desiredComponent), acc)
  }
